// src/components/billing/CurrentPlanPanel.jsx — the arrangement in force (a slim strip, not a card).
//
// ⛔⛔ EVERYTHING HERE IS READ FROM THE SERVER ON EVERY RENDER. Never keep
// "cancelling" or "change scheduled" in view state: a reload, or a second tab,
// makes local state wrong and the read already returns both.
//
// ⚠️ `cancel_at_period_end` is THREE-valued: null means Stripe has never told
// us, which is NOT "not cancelling" — so the Resume control keys off `=== true`.
//
// ⛔ Resume (and the declined-renewal card update) are hidden while `subscription_id` is null:
// an operator-assigned plan (free, special) was never bought through Stripe and
// answers 409 NOT_A_STRIPE_SUBSCRIPTION.
//
// ⭐ 2026-10-08: no longer a card («Il tuo piano» + name + «gestito dal tuo amministratore» are gone). It is a slim
// strip at the top of the plans card: the plan's name only when no grid card names it, and the
// scheduled-change / cancelling / updating lines — each only when it applies; nothing at all otherwise.
// ⭐ v203: the panel shows the page's bounded wait after a write Stripe reflects late — «Aggiornamento in corso…»
// while `settling`, then the quiet lag note while `lagging`. Its controls are disabled through `busy`, which the
// page passes as `busy || settling`.
// ⭐ v204 `payment_pending` (a payment waiting for the customer) → a banner, three variants: a «Passa ora» or a
// renewal waiting for the bank's 3-D Secure confirmation («Conferma il pagamento» → Stripe's page in a new tab, via
// `onConfirmPayment(url)`; a link that is not https gets no button and names the email instead), or a declined
// renewal Stripe is retrying («Aggiorna il metodo di pagamento»). ⚠️ Its `null` means NOTHING is waiting (the
// backend's own record), not "unknown"; an unknown reason/kind renders nothing.
// ⭐ v204: «Aggiorna il metodo di pagamento» and «Disdici» are no longer here — they are chips next to the page title
// (BillingTitleChips.jsx, which also owns the rule for when «Disdici» shows). The declined-renewal banner keeps its
// own «Aggiorna il metodo di pagamento»: it belongs to that message.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';
import { safeHttpsUrl } from '../../billing/stripeLink.js';
import { formatRenewalDate, formatRenewalTime } from '../../utils/creditsFormat.js';

// `scheduledPlanName` / `scheduledDate` are optional display forms the screen
// resolves (the name from `options[]`, the date formatted); both fall back to
// the raw wire values, so the banner never depends on them.
// `currentHasCard` (default true): the page says whether the plan in force has a card in the grid. When it does,
// the card's «Abbonamento attuale» badge already names it; when it does not (Free, Special — hidden rows) this strip names
// it. No plan at all is always named («Nessun abbonamento attivo»).
export default function CurrentPlanPanel({
  data, busy, onResume, onCancelChange, onUpdateCard, onConfirmPayment = () => {},
  scheduledPlanName, scheduledDate, settling = false, lagging = false, currentHasCard = true, pendingPlanName,
}) {
  const { t } = useTranslation('billing');
  const stripeManaged = !!data.subscription_id;
  const cancelling = data.cancel_at_period_end === true;   // ⛔ not `!== false`
  const scheduled = !!data.scheduled_plan_id;
  const banner = pendingBanner(t, data.payment_pending, pendingPlanName);
  const planLine = !data.plan_id
    ? t('current.none')
    : (!currentHasCard ? t('current.planLabel', { plan: data.plan_display_name || data.plan_id }) : null);

  // ⭐ Nothing to say → nothing rendered (an operator plan that has its card, nothing pending).
  if (!planLine && !scheduled && !cancelling && !settling && !lagging && !banner) return null;

  return (
    <div style={{ fontFamily: FONT_SANS, margin: '0 0 20px' }}>
      {planLine && <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.ink }}>{planLine}</span>}

      {settling && (
        <p role="status" style={{ fontSize: 12, color: COLORS.textSecondary, margin: '8px 0 0' }}>
          {t('current.settling')}
        </p>
      )}
      {!settling && lagging && (
        <p style={{ fontSize: 12, color: COLORS.textSecondary, margin: '8px 0 0' }}>{t('current.lagNote')}</p>
      )}

      {banner && (
        <div role="status" style={{ marginTop: 10, padding: '10px 12px', borderRadius: RADII.controlInner,
                      background: SEMANTIC.attenzione.bg, color: SEMANTIC.attenzione.fg, fontSize: 12 }}>
          {banner.text}
          {banner.action === 'confirm' && (
            <button type="button" onClick={() => onConfirmPayment(banner.url)} disabled={busy}
                    style={linkBtn}>{t('current.confirmPayment')}</button>
          )}
          {banner.action === 'card' && stripeManaged && (
            <button type="button" onClick={onUpdateCard} disabled={busy}
                    style={linkBtn}>{t('current.updateCard')}</button>
          )}
        </div>
      )}

      {scheduled && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: RADII.controlInner,
                      background: SEMANTIC.info.bg, color: SEMANTIC.info.fg, fontSize: 12 }}>
          {t('current.scheduled', {
            plan: scheduledPlanName || data.scheduled_plan_id,
            date: scheduledDate || data.scheduled_at,
          })}
          <button type="button" onClick={onCancelChange} disabled={busy}
                  style={linkBtn}>{t('current.cancelChange')}</button>
        </div>
      )}

      {cancelling && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: RADII.controlInner,
                      background: SEMANTIC.attenzione.bg, color: SEMANTIC.attenzione.fg, fontSize: 12 }}>
          {t('current.cancelling')}
          {/* ⛔ Resume is a Stripe control too: hidden while subscription_id is null. */}
          {stripeManaged && (
            <button type="button" onClick={onResume} disabled={busy}
                    style={linkBtn}>{t('current.resume')}</button>
          )}
        </div>
      )}
    </div>
  );
}

/** The banner for `payment_pending`: `{ text, action: 'confirm'|'card'|null, url }`, or null (nothing / unknown). */
function pendingBanner(t, pending, planName) {
  if (!pending || typeof pending !== 'object') return null;
  const url = safeHttpsUrl(pending.hosted_invoice_url);
  const withLink = (text) => (url
    ? { text, action: 'confirm', url }
    : { text: `${text} ${t('errors.paymentLinkEmailed')}`, action: null, url: null });
  if (pending.reason === 'ACTION_REQUIRED' && pending.kind === 'PLAN_CHANGE') {
    const plan = planName || pending.plan_id;
    const date = formatRenewalDate(pending.expires_at);
    const time = formatRenewalTime(pending.expires_at);
    return withLink(date && time
      ? t('current.pending.planChange', { plan, date, time })
      : t('current.pending.planChangeNoDate', { plan }));
  }
  if (pending.reason === 'ACTION_REQUIRED' && pending.kind === 'RENEWAL') {
    return withLink(t('current.pending.renewalAction'));
  }
  if (pending.reason === 'PAYMENT_FAILED' && pending.kind === 'RENEWAL') {
    return { text: t('current.pending.renewalFailed'), action: 'card', url: null };
  }
  return null;
}

const linkBtn = {
  marginLeft: 8, border: 'none', background: 'none', padding: 0,
  color: 'inherit', fontWeight: 700, textDecoration: 'underline',
  cursor: 'pointer', fontFamily: FONT_SANS, fontSize: 12,
};
